import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/use-auth'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Menu,
  Loader2,
  Sparkles,
  LayoutDashboard,
  CreditCard,
  LogOut,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { getSubscription } from '@/services/subscription'
import { ThemeToggle } from '@/components/ThemeToggle'

const UserNav = ({ handleSignOut }: { handleSignOut: () => Promise<void> }) => {
  const { user } = useAuth()
  const userInitial = user?.email?.charAt(0).toUpperCase() || 'U'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-8 w-8 rounded-full">
          <Avatar className="h-8 w-8">
            <AvatarFallback>{userInitial}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">Minha Conta</p>
            <p className="text-xs leading-none text-muted-foreground">
              {user?.email}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/dashboard">
            <LayoutDashboard className="mr-2 h-4 w-4" />
            <span>Dashboard</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/subscription-management">
            <CreditCard className="mr-2 h-4 w-4" />
            <span>Assinatura</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut}>
          <LogOut className="mr-2 h-4 w-4" />
          <span>Sair</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export const Header = () => {
  const { user, signOut, loading } = useAuth()
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [subscription, setSubscription] = useState<any>(null)
  const location = useLocation()
  const navigate = useNavigate()
  const isLandingPage = location.pathname === '/'

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    if (user) {
      getSubscription().then(setSubscription)
    } else {
      setSubscription(null)
    }
  }, [user, location.pathname])

  const handleSignOut = async () => {
    await signOut()
    setIsMenuOpen(false)
    navigate('/')
  }

  const handleLogoClick = () => {
    if (isLandingPage) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      navigate(user ? '/dashboard' : '/')
    }
  }

  const renderNavContent = (isMobile = false) => {
    const commonProps = {
      onClick: () => isMobile && setIsMenuOpen(false),
    }

    if (loading) return <Loader2 className="animate-spin" />

    if (user) {
      return (
        <>
          {isMobile && (
            <>
              <Button variant="ghost" asChild className="w-full">
                <Link to="/dashboard" {...commonProps}>
                  Dashboard
                </Link>
              </Button>
              <Button variant="ghost" asChild className="w-full">
                <Link to="/subscription-management" {...commonProps}>
                  Assinatura
                </Link>
              </Button>
            </>
          )}
          {!subscription && (
            <Button variant="outline" size="sm" asChild>
              <Link to="/subscribe" {...commonProps}>
                <Sparkles className="mr-2 h-4 w-4 text-yellow-400" />
                Assinar
              </Link>
            </Button>
          )}
          {isMobile ? (
            <Button variant="ghost" onClick={handleSignOut} className="w-full">
              Sair
            </Button>
          ) : (
            <UserNav handleSignOut={handleSignOut} />
          )}
        </>
      )
    }

    return (
      <>
        <Button variant="ghost" asChild>
          <Link to="/login" {...commonProps}>
            Acessar
          </Link>
        </Button>
        <Button asChild>
          <Link to="/signup" {...commonProps}>
            Criar Conta
          </Link>
        </Button>
      </>
    )
  }

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        (isScrolled && isLandingPage) || !isLandingPage
          ? 'bg-background/90 backdrop-blur-sm border-b'
          : 'border-b border-transparent',
      )}
    >
      <div className="container mx-auto flex h-[72px] items-center justify-between px-4 md:px-6">
        <button
          onClick={handleLogoClick}
          className="text-xl font-semibold text-foreground"
        >
          Briefly
        </button>
        <div className="hidden md:flex items-center gap-2">
          {renderNavContent()}
          <ThemeToggle />
        </div>
        <div className="md:hidden flex items-center">
          <ThemeToggle />
          <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu />
              </Button>
            </SheetTrigger>
            <SheetContent side="right">
              <div className="flex flex-col items-center justify-center h-full gap-6">
                {renderNavContent(true)}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  )
}
